import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react"
import { graphql, useStaticQuery } from "gatsby"
import {
  convert as convertAmount,
  formatMoney,
  fetchRates,
  fetchCurrencyNames,
  sortCurrencies,
  CACHE_KEY,
  PREF_KEY,
  MAX_AGE_MS,
} from "../lib/currency"

const CurrencyContext = createContext(null)

// Exported for tests that need to drive the picker with a controlled value.
export { CurrencyContext }

export function useCurrency() {
  return useContext(CurrencyContext)
}

export function CurrencyProvider({ children }) {
  const data = useStaticQuery(graphql`
    query CurrencySettings {
      sanityCurrencySettings {
        defaultCurrency
      }
    }
  `)
  // Base currency comes from Sanity (IDR). Prices are stored in it, and it's
  // the default the picker starts on / the value shown when rates are down.
  const base = (data.sanityCurrencySettings?.defaultCurrency || "USD").toUpperCase()

  // status: "loading" | "ready" | "unavailable"
  const [status, setStatus] = useState("loading")
  const [rates, setRates] = useState(null)
  const [names, setNames] = useState({})
  const [selected, setSelected] = useState(base)

  useEffect(() => {
    let cancelled = false

    // Restore a previously chosen currency.
    try {
      const pref = window.localStorage.getItem(PREF_KEY)
      if (pref) setSelected(pref.toUpperCase())
    } catch (e) {}

    const readCache = () => {
      try {
        const c = JSON.parse(window.localStorage.getItem(CACHE_KEY) || "null")
        if (c && c.base === base && c.rates) return c
      } catch (e) {}
      return null
    }

    async function load() {
      // Currency names are non-critical (search labels) — load in the background.
      fetchCurrencyNames().then(n => {
        if (!cancelled && n) setNames(n)
      })

      // 1. Fresh enough cache? Use it, skip the network.
      const cached = readCache()
      if (cached && Date.now() - cached.fetchedAt < MAX_AGE_MS) {
        if (!cancelled) {
          setRates(cached.rates)
          setStatus("ready")
        }
        return
      }

      // 2. Fetch fresh rates.
      const result = await fetchRates(base)
      if (cancelled) return
      if (result) {
        setRates(result.rates)
        setStatus("ready")
        try {
          window.localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({
              base,
              rates: result.rates,
              date: result.date,
              fetchedAt: Date.now(),
            })
          )
        } catch (e) {}
        return
      }

      // 3. Stale cache still beats nothing.
      if (cached) {
        setRates(cached.rates)
        setStatus("ready")
        return
      }

      // 4. Nothing worked: disable the picker, show base currency only.
      setSelected(base)
      setStatus("unavailable")
    }

    load()
    return () => {
      cancelled = true
    }
  }, [base])

  const selectCurrency = useCallback(code => {
    const c = code.toUpperCase()
    setSelected(c)
    try {
      window.localStorage.setItem(PREF_KEY, c)
    } catch (e) {}
  }, [])

  // Every currency we have a rate for, base first, then alphabetical.
  const currencies = useMemo(() => {
    if (!rates) return [{ code: base, name: names[base.toLowerCase()] || base }]
    const codes = new Set([base.toLowerCase(), ...Object.keys(rates)])
    const list = [...codes]
      .filter(c => c === base.toLowerCase() || rates[c])
      .map(c => ({ code: c.toUpperCase(), name: names[c] || c.toUpperCase() }))
    return sortCurrencies(list, base)
  }, [rates, names, base])

  const format = useCallback(
    amount => formatMoney(amount, { base, selected, rates, status }),
    [base, selected, rates, status]
  )
  const convert = useCallback(
    amount => convertAmount(amount, { base, selected, rates }),
    [base, selected, rates]
  )

  const value = useMemo(
    () => ({
      base,
      selected,
      currencies,
      status,
      enabled: status === "ready" && currencies.length > 1,
      selectCurrency,
      format,
      convert,
    }),
    [base, selected, currencies, status, selectCurrency, format, convert]
  )

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  )
}
